import React, { useState, useRef, useCallback } from 'react';
import { faUpload, faTrash, faEdit, faImage, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from './Button';
import ImageCropper from './ImageCropper';
import { LogoService } from '../services/logoService';
import { validateImageFile } from '../utils/imageUtils';

interface LogoUploadProps {
  organizationId: string;
  currentLogoUrl?: string;
  onLogoChange: (logoUrl: string | null) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  organizationId,
  currentLogoUrl,
  onLogoChange,
  onError,
  onSuccess
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (file: File) => {
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        onError(validation.error || 'Arquivo inválido');
        return;
      }

      setSelectedFile(file);
      setShowCropper(true);
    },
    [onError]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleCropComplete = useCallback(
    async (croppedBlob: Blob) => {
      setIsUploading(true);
      setShowCropper(false);

      try {
        const result = await LogoService.replaceOrganizationLogo(
          organizationId,
          croppedBlob,
          currentLogoUrl
        );

        if (result.success) {
          onLogoChange(result.logoUrl || null);
          onSuccess(result.message);
        } else {
          onError(result.message);
        }
      } catch (error) {
        onError('Erro ao fazer upload do logo');
      } finally {
        setIsUploading(false);
        setSelectedFile(null);
      }
    },
    [organizationId, currentLogoUrl, onLogoChange, onError, onSuccess]
  );

  const handleRemoveLogo = useCallback(async () => {
    if (!currentLogoUrl) return;

    setIsUploading(true);
    try {
      const result = await LogoService.removeOrganizationLogo(organizationId, currentLogoUrl);

      if (result.success) {
        onLogoChange(null);
        onSuccess(result.message);
      } else {
        onError(result.message);
      }
    } catch (error) {
      onError('Erro ao remover logo');
    } finally {
      setIsUploading(false);
    }
  }, [organizationId, currentLogoUrl, onLogoChange, onError, onSuccess]);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-700">Logo da Organização</div>

      {/* Current Logo Preview */}
      {currentLogoUrl && (
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border">
            <img src={currentLogoUrl} alt="Logo atual" className="w-full h-full object-cover" />
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={openFileDialog}
              variant="secondary"
              size="sm"
              leftIcon={faEdit}
              disabled={isUploading}
            >
              Alterar
            </Button>
            <Button
              onClick={handleRemoveLogo}
              variant="danger"
              size="sm"
              leftIcon={faTrash}
              disabled={isUploading}
            >
              Remover
            </Button>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {!currentLogoUrl && (
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
            ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            }
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={!isUploading ? openFileDialog : undefined}
        >
          {isUploading ? (
            <div className="space-y-2">
              <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-500 animate-spin" />
              <div className="text-sm text-gray-600">Fazendo upload...</div>
            </div>
          ) : (
            <div className="space-y-2">
              <FontAwesomeIcon icon={faImage} className="w-8 h-8 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  Clique para escolher ou arraste uma imagem
                </div>
                <div className="text-xs text-gray-500">PNG, JPG, JPEG ou SVG até 5MB</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isUploading}
      />

      {/* Upload Button (alternative) */}
      {!currentLogoUrl && !isUploading && (
        <div className="text-center">
          <Button onClick={openFileDialog} variant="outline" leftIcon={faUpload}>
            Escolher Arquivo
          </Button>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && selectedFile && (
        <ImageCropper
          isOpen={showCropper}
          onClose={() => {
            setShowCropper(false);
            setSelectedFile(null);
          }}
          onCropComplete={handleCropComplete}
          imageFile={selectedFile}
        />
      )}

      {/* Instructions */}
      <div className="text-xs text-gray-500">
        <p>• O logo será automaticamente redimensionado para um formato quadrado</p>
        <p>• Para melhores resultados, use imagens com pelo menos 256x256 pixels</p>
        <p>• Formatos suportados: PNG, JPG, JPEG, SVG</p>
        <p>• Tamanho máximo: 5MB</p>
      </div>
    </div>
  );
};

export default LogoUpload;
